import React from 'react';
import { CreditCard, FileText, Layers, Repeat2, UserRound } from 'lucide-react';
import type {
  DashboardContextResultPayload,
  DashboardNavigationResultPayload,
  DashboardSearchResultPayload,
  DashboardToolErrorPayload,
} from '../types';
import {
  formatCustomerFacingFormType,
  formatSearchCurrency,
  formatSearchDateInTimezone,
  getNumberValue,
  getStringValue,
} from '../lib/searchFormatters';
import { getDashboardPathLabel } from '../lib/dashboardPathLabels';
import { normalizeContactRecord } from '../../../../utils/contactRecord';

function SearchResultSection({
  icon,
  items,
  renderItem,
  title,
}: {
  icon: React.ReactNode;
  items: Array<Record<string, unknown>>;
  renderItem: (item: Record<string, unknown>, index: number) => React.ReactNode;
  title: string;
}) {
  if (!items.length) {
    return null;
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-ink-muted">
        {icon}
        {title}
      </div>
      <div className="space-y-1.5">{items.map(renderItem)}</div>
    </div>
  );
}

export function DashboardSearchResultsMessage({
  dashboardTimezone,
  payload,
}: {
  dashboardTimezone?: string | null;
  payload: DashboardSearchResultPayload;
}) {
  const results = payload.results || {};
  const contacts = results.contacts || [];
  const recentPayments = results.recentPayments || [];
  const subscriptions = results.subscriptions || [];
  const forms = results.forms || [];
  const elements = results.elements || [];
  const resultCount =
    contacts.length + recentPayments.length + subscriptions.length + forms.length + elements.length;

  return (
    <div className="w-full space-y-3" data-testid="dashboard-search-results">
      <div>
        <div className="text-sm font-semibold text-ink">Search results for "{payload.query}"</div>
        <div className="mt-0.5 text-xs text-ink-muted">
          {resultCount === 0
            ? 'No matching dashboard records found.'
            : `${resultCount} matching record${resultCount === 1 ? '' : 's'} found`}
          {payload.organizationName ? ` in ${payload.organizationName}` : ''}
        </div>
      </div>

      <SearchResultSection
        title="Contacts"
        icon={<UserRound className="h-3.5 w-3.5" />}
        items={contacts}
        renderItem={(contact, index) => {
          const name = normalizeContactRecord(contact).displayName;
          const email = getStringValue(contact, 'email');
          const phone = getStringValue(contact, 'phone');
          const address = getStringValue(contact, 'address');
          return (
            <div
              key={`${getStringValue(contact, 'contactId') || name}-${index}`}
              className="rounded-lg border border-edge/70 bg-white px-3 py-2"
            >
              <div className="truncate text-sm font-medium text-ink">{name}</div>
              {email ? <div className="truncate text-xs text-ink-muted">{email}</div> : null}
              {phone ? <div className="truncate text-xs text-ink-muted">{phone}</div> : null}
              {address ? <div className="truncate text-xs text-ink-muted">{address}</div> : null}
            </div>
          );
        }}
      />

      <SearchResultSection
        title="Recent Payments"
        icon={<CreditCard className="h-3.5 w-3.5" />}
        items={recentPayments}
        renderItem={(payment, index) => {
          const amount = formatSearchCurrency(getNumberValue(payment, 'totalCharged'));
          const state = getStringValue(payment, 'state');
          const timestamp =
            getStringValue(payment, 'timestampFormatted') ||
            formatSearchDateInTimezone(getStringValue(payment, 'timestamp'), dashboardTimezone);
          return (
            <div
              key={`${getStringValue(payment, 'transactionId') || index}`}
              className="rounded-lg border border-edge/70 bg-white px-3 py-2"
            >
              <div className="flex min-w-0 items-center gap-2">
                <span className="truncate text-sm font-medium text-ink">
                  {getStringValue(payment, 'donorName') || 'Payment'}
                </span>
                {amount ? (
                  <span className="shrink-0 text-xs font-medium text-ink">{amount}</span>
                ) : null}
              </div>
              <div className="mt-0.5 flex flex-wrap gap-x-2 gap-y-1 text-xs text-ink-muted">
                {state ? <span>{state}</span> : null}
                {timestamp ? <span>{timestamp}</span> : null}
                {getStringValue(payment, 'email') ? (
                  <span>{getStringValue(payment, 'email')}</span>
                ) : null}
              </div>
            </div>
          );
        }}
      />

      <SearchResultSection
        title="Subscriptions"
        icon={<Repeat2 className="h-3.5 w-3.5" />}
        items={subscriptions}
        renderItem={(subscription, index) => {
          const amount = formatSearchCurrency(getNumberValue(subscription, 'totalCharged'));
          return (
            <div
              key={`${getStringValue(subscription, 'subscriptionId') || index}`}
              className="rounded-lg border border-edge/70 bg-white px-3 py-2"
            >
              <div className="flex min-w-0 items-center gap-2">
                <span className="truncate text-sm font-medium text-ink">
                  {getStringValue(subscription, 'donorName') || 'Subscription'}
                </span>
                {amount ? (
                  <span className="shrink-0 text-xs font-medium text-ink">{amount}</span>
                ) : null}
              </div>
              <div className="mt-0.5 flex flex-wrap gap-x-2 gap-y-1 text-xs text-ink-muted">
                {getStringValue(subscription, 'status') ? (
                  <span>{getStringValue(subscription, 'status')}</span>
                ) : null}
                {getStringValue(subscription, 'frequency') ? (
                  <span>{getStringValue(subscription, 'frequency')}</span>
                ) : null}
                {getStringValue(subscription, 'email') ? (
                  <span>{getStringValue(subscription, 'email')}</span>
                ) : null}
              </div>
            </div>
          );
        }}
      />

      <SearchResultSection
        title="Forms"
        icon={<FileText className="h-3.5 w-3.5" />}
        items={forms}
        renderItem={(form, index) => (
          <div
            key={`${getStringValue(form, 'formId') || index}`}
            className="rounded-lg border border-edge/70 bg-white px-3 py-2"
          >
            <div className="truncate text-sm font-medium text-ink">
              {getStringValue(form, 'name') || 'Untitled form'}
            </div>
            <div className="mt-0.5 text-xs text-ink-muted">
              {formatCustomerFacingFormType(getStringValue(form, 'type'))}
            </div>
          </div>
        )}
      />

      <SearchResultSection
        title="Elements"
        icon={<Layers className="h-3.5 w-3.5" />}
        items={elements}
        renderItem={(element, index) => (
          <div
            key={`${getStringValue(element, 'elementId') || index}`}
            className="rounded-lg border border-edge/70 bg-white px-3 py-2"
          >
            <div className="truncate text-sm font-medium text-ink">
              {getStringValue(element, 'name') || 'Untitled element'}
            </div>
            <div className="mt-0.5 text-xs text-ink-muted">
              {getStringValue(element, 'type') || 'Element'}
            </div>
          </div>
        )}
      />
    </div>
  );
}

// Note: The three components below are declared but currently unused. They appear to be
// alternate renderers for activity-feed messages; the active path uses the consolidated
// activity grouping in ActivityGroup.tsx. Preserved during refactor to maintain the
// public surface; consider removing as a follow-up if confirmed dead.

export function DashboardNavigationResultMessage({
  payload,
}: {
  payload: DashboardNavigationResultPayload;
}) {
  const path = payload.path || '';

  return (
    <div className="flex w-full justify-start">
      <div className="rounded-full bg-paper-light px-2.5 py-1 text-[11px] leading-5 text-ink-muted">
        Opened <span className="font-medium text-ink">{getDashboardPathLabel(path)}</span>
      </div>
    </div>
  );
}

export function DashboardContextResultMessage({
  payload,
}: {
  payload: DashboardContextResultPayload;
}) {
  const feature = payload.currentFeature || payload.route?.currentFeature;

  return (
    <div className="flex w-full justify-start">
      <div className="rounded-full bg-paper-light px-2.5 py-1 text-[11px] leading-5 text-ink-muted">
        Checked current page{feature ? `: ${feature}` : ''}
      </div>
    </div>
  );
}

export function DashboardToolErrorMessage({ payload }: { payload: DashboardToolErrorPayload }) {
  return (
    <div className="flex w-full justify-start">
      <div className="rounded-full bg-paper-light px-2.5 py-1 text-[11px] leading-5 text-ink-muted">
        {payload.message}
      </div>
    </div>
  );
}
